import classNames from "classnames";
import { Link } from "react-router-dom";
import { common_utils } from "../../../../../../common/common_utils";
import {
	getRawProductWithoutPrices,
	PaymentInterval,
	PriceDescriptor,
} from "../../../../../../common/premium_tiers";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { Tag } from "../base_components/tag";
import { InfoTooltip } from "../tooltips/info_tooltip";
import { centsToDollars } from "./pricing_table";
import { isFeatureEnabled } from "../../../../../../src/feature_flags";

type Coupon =
	RouterInferenceHelpers["premium"]["getAllProductsPrices"]["output"]["active_coupon"];

type BasePricingTierProps = {
	link_type: "anchor" | "Link";
	is_best_deal?: boolean;
	title: string;
	features: (string | JSX.Element)[];
	tag_type?: "Enterprise" | "Best deal";
};

type RegularPricingTierProps = BasePricingTierProps & {
	type: "regular";
	span: PaymentInterval;
	has_used_free_trial?: boolean;
	product_id: string;
	price?: PriceDescriptor;
	active_coupon?: Coupon;
};

type CustomPricingTierProps = BasePricingTierProps & {
	type: "custom";
	onContactClick: () => void;
};

export type PricingTierProps = RegularPricingTierProps | CustomPricingTierProps;

export function PricingTier(props: PricingTierProps) {
	const is_best_deal = props.is_best_deal ?? false;

	return (
		<div
			className={classNames(
				"flex w-full max-w-[17rem] flex-shrink-0 flex-col gap-4 whitespace-nowrap rounded-lg border-2 px-4 pb-8 pt-4 shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl sm:max-w-[20rem] sm:px-[1.9rem]",
				{
					"border-sesh-primary bg-gradient-to-b from-base-light via-base-light to-base-dark":
						is_best_deal,
					"border-content-secondary bg-gradient-to-b from-base-light to-base-light":
						!is_best_deal,
				}
			)}
		>
			<div className="flex min-h-[2rem] w-full">
				{props.type === "regular" && props.active_coupon && (
					<Tag type="Light" className="animate-bounce font-semibold">
						{props.active_coupon.api_data.percent_off}% off
					</Tag>
				)}
				{props.tag_type && (
					<Tag
						type="Dark"
						className={classNames("ml-auto", {
							"animate-pulse bg-premium font-bold !text-base-dark":
								props.tag_type === "Best deal",
							"bg-premium px-2 py-1 text-sm font-bold !text-base-dark":
								props.tag_type === "Enterprise",
						})}
					>
						{props.tag_type}
					</Tag>
				)}
			</div>
			<div className="mx-2 flex h-full flex-col">
				<div
					className={classNames(
						"mb-1 flex h-[4rem] flex-col items-center justify-center text-center"
					)}
				>
					{props.title === "Premium Bundle" ? (
						<div className="flex flex-col items-center">
							<i
								className={classNames(
									"fas fa-gem text-sm sm:text-base",
									{
										"text-sesh-primary": true,
										"text-content-primary": false,
									}
								)}
							></i>
							<div className="-mt-0.5 flex gap-1">
								<i
									className={classNames(
										"fas fa-gem text-sm sm:text-base",
										{
											"text-sesh-primary": true,
											"text-content-primary": false,
										}
									)}
								></i>
								<i
									className={classNames(
										"fas fa-gem text-sm sm:text-base",
										{
											"text-sesh-primary": true,
											"text-content-primary": false,
										}
									)}
								></i>
							</div>
						</div>
					) : (
						<i
							className={classNames(
								"fas",
								props.type === "custom" ? "fa-robot" : "fa-gem",
								"text-xl sm:text-2xl"
							)}
						></i>
					)}
					<div
						className={classNames(
							"mt-1 text-xl font-extrabold sm:text-2xl",
							{
								"text-sesh-primary":
									props.title === "Premium Bundle",
								"text-content-primary":
									props.title !== "Premium Bundle",
							}
						)}
					>
						{props.title}
					</div>
				</div>
				<div
					className={classNames("mb-2 border-b pb-1", {
						"border-sesh-primary": is_best_deal,
						"border-content-secondary": !is_best_deal,
					})}
				>
					{props.type === "regular" && props.price && (
						<FullPriceDisplay
							price={props.price}
							span={props.span}
							coupon={props.active_coupon}
							is_best_deal={is_best_deal}
							type="regular"
						/>
					)}
					{props.type === "custom" && (
						<FullPriceDisplay
							price={{ cents: 0, id: "custom" }}
							span={PaymentInterval.Month}
							is_best_deal={is_best_deal}
							type="custom"
						/>
					)}
				</div>
				<div className="flex-grow text-sm sm:text-base">
					<ul className="min-h-[16rem]">
						{props.features.map((feature, index) => (
							<li
								key={index}
								className="mt-2 flex items-start gap-2"
							>
								<i
									className={classNames(
										"fas fa-check-circle mt-0.5 text-lg",
										{
											"text-success-primary":
												is_best_deal,
											"text-content-primary":
												!is_best_deal,
										}
									)}
									title="Active"
									suppressHydrationWarning={true}
								></i>{" "}
								<span className="text-content-primary">
									{feature}
								</span>
							</li>
						))}
					</ul>
				</div>
				{props.type === "regular" ? (
					<>
						<div className="mt-auto pt-8">
							{props.link_type === "Link" ? (
								<Link
									to={common_utils.getStripeRedirectURL({
										type: "checkout",
										product_id: props.product_id,
										subscription_span:
											props.span.toString(),
										should_force_no_coupon: "false",
									})}
									className="mt-2"
								>
									<SeshPrimaryButton
										className={classNames(
											"justify-center font-extrabold",
											{
												"bg-sesh-primary !text-base-dark transition-all duration-300 hover:bg-opacity-90 hover:!text-white":
													is_best_deal,
												"border-2 border-content-primary !text-content-primary transition-all duration-300 hover:bg-content-primary hover:!text-base-dark":
													!is_best_deal,
											}
										)}
									>
										{props.span === PaymentInterval.OneTime
											? "Get Access Now"
											: props.has_used_free_trial ||
											  !isFeatureEnabled("FreeTrial")
											? "Subscribe Now"
											: "Start Free Trial"}
									</SeshPrimaryButton>
								</Link>
							) : (
								<a
									href={common_utils.getStripeRedirectURL({
										type: "checkout",
										product_id: props.product_id,
										subscription_span:
											props.span.toString(),
										should_force_no_coupon: "false",
									})}
									className="mt-2"
								>
									<SeshPrimaryButton
										className={classNames(
											"justify-center font-extrabold",
											{
												"bg-sesh-primary !text-base-dark transition-all duration-300 hover:bg-opacity-90 hover:!text-white":
													is_best_deal,
												"border-2 border-content-primary !text-content-primary transition-all duration-300 hover:bg-content-primary hover:!text-base-dark":
													!is_best_deal,
											}
										)}
									>
										{props.span === PaymentInterval.OneTime
											? "Get Access Now"
											: props.has_used_free_trial ||
											  !isFeatureEnabled("FreeTrial")
											? "Subscribe Now"
											: "Start Free Trial"}
									</SeshPrimaryButton>
								</a>
							)}
						</div>
					</>
				) : (
					<>
						<div className="mt-auto">
							<SeshPrimaryButton
								onClick={props.onContactClick}
								className={classNames(
									"justify-center font-extrabold",
									{
										"bg-sesh-primary !text-base-dark transition-all duration-300 hover:bg-opacity-90 hover:!text-white":
											is_best_deal,
										"border-2 border-content-primary !text-content-primary transition-all duration-300 hover:bg-content-primary hover:!text-base-dark":
											!is_best_deal,
									}
								)}
							>
								Get Started
							</SeshPrimaryButton>
						</div>
					</>
				)}
			</div>
		</div>
	);
}

function FullPriceDisplay(props: {
	price: PriceDescriptor;
	coupon?: Coupon;
	span: PaymentInterval;
	is_best_deal?: boolean;
	type?: "regular" | "custom";
}) {
	const { price, coupon, span, is_best_deal, type } = props;

	return (
		<div className="flex flex-col">
			<PriceDisplay
				cents={price.cents}
				span={span}
				striked_through={Boolean(coupon)}
				is_best_deal={is_best_deal}
				type={type}
			/>
			{coupon && (
				<PriceDisplay
					cents={
						price.cents * (1 - coupon.api_data.percent_off / 100)
					}
					span={span}
					is_best_deal={is_best_deal}
					is_discounted={true}
					type={type}
				/>
			)}
		</div>
	);
}

function PriceDisplay(props: {
	cents: number;
	span: PaymentInterval;
	striked_through?: boolean;
	is_best_deal?: boolean;
	is_discounted?: boolean;
	type?: "regular" | "custom";
}) {
	const { cents, span, type } = props;

	// For annual plans, calculate and show monthly cost
	const displayCents = span === PaymentInterval.Year ? cents / 12 : cents;

	return (
		<div
			className={classNames(
				"mb-2 flex flex-col items-center justify-center",
				{
					"opacity-25": props.striked_through,
					"text-sesh-primary":
						props.is_best_deal && !props.striked_through,
					"text-content-primary":
						!props.is_best_deal && !props.striked_through,
				}
			)}
		>
			<div className="flex items-center justify-center gap-1">
				{type === "custom" ? (
					<div className="text-4xl font-extrabold text-content-primary">
						Contact Us
					</div>
				) : (
					<>
						<div
							className={classNames("text-4xl font-extrabold", {
								"font-normal line-through decoration-2":
									props.striked_through,
								"animate-pulse": props.is_discounted,
							})}
						>
							${centsToDollars(displayCents)}
						</div>
						{(span !== PaymentInterval.OneTime && (
							<div
								className={classNames("text-xs font-bold", {
									"line-through": props.striked_through,
								})}
							>
								{span === PaymentInterval.Year ? (
									<>
										per
										<br />
										month
									</>
								) : (
									<>
										per
										<br />
										{PaymentInterval[span].toLowerCase()}
									</>
								)}
							</div>
						)) || (
							<div
								className={classNames("text-xs font-bold", {
									"line-through": props.striked_through,
								})}
							>
								one
								<br />
								time
							</div>
						)}
					</>
				)}
			</div>
			<div
				className={classNames("text-xs", {
					invisible:
						span !== PaymentInterval.Year || type === "custom",
					"text-content-secondary":
						span === PaymentInterval.Year && type !== "custom",
				})}
				aria-hidden={span !== PaymentInterval.Year || type === "custom"}
			>
				Billed Annually
			</div>
		</div>
	);
}
